
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Team {
  id: string;
  name: string;
  photo?: string;
}

interface Game {
  id: string;
  home_team: string;
  away_team: string;
  date: string;
  time: string;
  location: string;
  jornada?: string; // optional field used in the modified view
}

interface Result {
  id: string;
  game_id: string;
  home_team: string;
  away_team: string;
  home_score: number;
  away_score: number;
  status: string;
  created_at: string;
  // Additional optional fields that might be used in the modified view
  jornada?: string;
  date?: string;
  location?: string;
}

export default function ResultsManager() {
  const [teams, setTeams] = useState<Team[]>([]);
  // Estado para equipos externos (opponent_teams)
  const [opponentTeams, setOpponentTeams] = useState<
    { id: string; name: string; logo?: string }[]
  >([]);
  const [games, setGames] = useState<Game[]>([]);
  const [results, setResults] = useState<Result[]>([]);
  const [showResultForm, setShowResultForm] = useState(false);
  const [editingResult, setEditingResult] = useState<Result | null>(null);

  const [resultForm, setResultForm] = useState({
    game_id: '',
    home_team: '',
    away_team: '',
    home_score: 0,
    away_score: 0,
    status: 'final',
  });

  /* --------------------------------------------------------------------- */
  /*                              FETCH DATA                               */
  /* --------------------------------------------------------------------- */
  useEffect(() => {
    fetchAllTeams();
    fetchGames();
    fetchResults();
  }, []);

  // Cargar equipos (locales y externos) -------------------------------------------------
  const fetchAllTeams = async () => {
    try {
      // Teams table
      const { data: teamsData, error: teamsError } = await supabase
        .from('teams')
        .select('id, name, photo')
        .order('name', { ascending: true });

      if (teamsError) throw teamsError;

      // Opponent_teams table
      const { data: opponentTeamsData, error: opponentError } = await supabase
        .from('opponent_teams')
        .select('id, name, logo')
        .order('name', { ascending: true });

      if (opponentError) throw opponentError;

      // Guardar los equipos externos por separado (se usan para obtener logos)
      setOpponentTeams(
        (opponentTeamsData || []).map((t) => ({
          id: t.id,
          name: t.name,
          logo: t.logo,
        }))
      );

      // Unir ambas fuentes en una lista única de equipos con la misma estructura
      const allTeams: Team[] = [
        ...(teamsData || []).map((t) => ({
          id: t.id,
          name: t.name,
          photo: t.photo,
        })),
        ...(opponentTeamsData || []).map((t) => ({
          id: t.id,
          name: t.name,
          photo: t.logo, // usamos la columna logo como "photo" para que la UI sea coherente
        })),
      ];

      setTeams(allTeams);
    } catch (error) {
      console.error('Error al cargar equipos:', error);
    }
  };

  // Obtener todos los partidos -------------------------------------------------
  const fetchGames = async () => {
    try {
      const { data, error } = await supabase
        .from('games')
        .select('*')
        .order('date', { ascending: true });

      if (error) throw error;
      setGames(data || []);
    } catch (error) {
      console.error('Error al cargar partidos:', error);
    }
  };

  // Obtener todos los resultados -------------------------------------------------
  const fetchResults = async () => {
    try {
      const { data, error } = await supabase
        .from('results')
        .select('*')
        .order('created_at', { ascending: false }); // Ya estaba correcto - más recientes primero

      if (error) throw error;
      setResults(data || []);
    } catch (error) {
      console.error('Error al cargar resultados:', error);
    }
  };

  /* --------------------------------------------------------------------- */
  /*                         UTILIDADES / HELPERS                           */
  /* --------------------------------------------------------------------- */

  // Verifica si un partido ya se jugó (fecha + hora frente a la hora actual)
  const isGamePlayed = (gameDate: string, gameTime: string) => {
    const now = new Date();
    const gameDateTime = new Date(`${gameDate}T${gameTime}`);
    return gameDateTime < now;
  };

  // Lista de partidos que ya se jugaron (para que el usuario solo pueda seleccionar)
  const getGameDateTime = (game: Game) => {
    const time = game.time || '00:00';
    return new Date(`${game.date}T${time}`);
  };

  const playedGames = games
    .filter((game) => isGamePlayed(game.date, game.time))
    .sort(
      (a, b) => getGameDateTime(b).getTime() - getGameDateTime(a).getTime()
    );

  // Formatea fechas en castellano
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Función para determinar si es victoria, derrota o empate para Viubàsquet
  const getResultType = (homeTeam: string, awayTeam: string, homeScore: number, awayScore: number) => {
    const isViubasquetHome = homeTeam.toLowerCase().includes('viubàsquet') || homeTeam.toLowerCase().includes('viubasquet');
    const isViubasquetAway = awayTeam.toLowerCase().includes('viubàsquet') || awayTeam.toLowerCase().includes('viubasquet');
    
    if (homeScore === awayScore) {
      return { type: 'empate', text: 'Empate' };
    }
    
    if (isViubasquetHome) {
      // Viubàsquet juega en casa
      if (homeScore > awayScore) {
        return { type: 'victoria', text: `Victoria ${homeTeam}` };
      } else {
        return { type: 'derrota', text: `Victoria ${awayTeam}` };
      }
    } else if (isViubasquetAway) {
      // Viubàsquet juega fuera
      if (awayScore > homeScore) {
        return { type: 'victoria', text: `Victoria ${awayTeam}` };
      } else {
        return { type: 'derrota', text: `Victoria ${homeTeam}` };
      }
    } else {
      // Partido sin Viubàsquet
      if (homeScore > awayScore) {
        return { type: 'neutral', text: `Victoria ${homeTeam}` };
      } else {
        return { type: 'neutral', text: `Victoria ${awayTeam}` };
      }
    }
  };

  /* --------------------------------------------------------------------- */
  /*                         HANDLERS / EVENTOS                             */
  /* --------------------------------------------------------------------- */

  // Cuando se elige un partido del <select>, rellenamos automáticamente los equipos
  const handleGameSelect = (gameId: string) => {
    const selectedGame = games.find((g) => g.id === gameId);
    if (selectedGame) {
      setResultForm({
        ...resultForm,
        game_id: gameId,
        home_team: selectedGame.home_team,
        away_team: selectedGame.away_team,
      });
    }
  };

  // Enviar (crear o actualizar) un resultado
  const handleResultSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Validaciones básicas de campos
      if (!resultForm.home_team || !resultForm.away_team) {
        alert('Por favor selecciona ambos equipos');
        return;
      }
      if (resultForm.home_team === resultForm.away_team) {
        alert('Los equipos local y visitante no pueden ser el mismo');
        return;
      }

      const resultData = {
        game_id: resultForm.game_id || null,
        home_team: resultForm.home_team,
        away_team: resultForm.away_team,
        home_score: Number(resultForm.home_score) || 0,
        away_score: Number(resultForm.away_score) || 0,
        status: resultForm.status,
      };

      console.log('Guardando resultado:', resultData);

      let response;
      if (editingResult) {
        // UPDATE
        response = await supabase
          .from('results')
          .update(resultData)
          .eq('id', editingResult.id)
          .select();
      } else {
        // INSERT
        response = await supabase
          .from('results')
          .insert([resultData])
          .select();
      }

      if (response.error) {
        console.error('Error de Supabase:', response.error);
        throw new Error(response.error.message || 'Error al guardar en la base de datos');
      }

      // Reset del formulario y cierre del modal
      setResultForm({
        game_id: '',
        home_team: '',
        away_team: '',
        home_score: 0,
        away_score: 0,
        status: 'final',
      });
      setShowResultForm(false);
      setEditingResult(null);
      await fetchResults();
      alert('Resultado guardado correctamente');
    } catch (error) {
      console.error('Error al guardar resultado:', error);

      // Normalización del mensaje de error
      let errorMessage = 'Error desconocido';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        // @ts-ignore – acceso dinámico a propiedades de posible objeto error
        if ('message' in error) errorMessage = String(error.message);
        else if ('details' in error) errorMessage = String(error.details);
        else if ('hint' in error) errorMessage = String(error.hint);
      }

      // Mensajes más claros al usuario
      if (errorMessage.includes('duplicate key')) {
        errorMessage = 'Ya existe un resultado para este partido';
      } else if (errorMessage.includes('foreign key')) {
        errorMessage = 'Error de referencia: verifica que los equipos existan';
      } else if (errorMessage.includes('not null')) {
        errorMessage = 'Faltan campos obligatorios';
      } else if (errorMessage.includes('check constraint')) {
        errorMessage = 'Los valores ingresados no son válidos';
      }

      alert(`Error al guardar resultado: ${errorMessage}`);
    }
  };

  // Editar un resultado existente
  const editResult = (result: Result) => {
    setEditingResult(result);
    setResultForm({
      game_id: result.game_id || '',
      home_team: result.home_team,
      away_team: result.away_team,
      home_score: result.home_score,
      away_score: result.away_score,
      status: result.status || 'final',
    });
    setShowResultForm(true);
  };

  // Eliminar un resultado
  const deleteResult = async (id: string) => {
    if (!window.confirm('¿Estás seguro de eliminar este resultado?')) return;

    try {
      const { error } = await supabase.from('results').delete().eq('id', id);
      if (error) {
        console.error('Error al eliminar resultado:', error);
        alert(`Error al eliminar resultado: ${error.message}`);
        return;
      }
      
      alert('Resultado eliminado correctamente');
      await fetchResults();
    } catch (error) {
      console.error('Error al eliminar resultado:', error);
      alert('Error al eliminar resultado');
    }
  };

  /* --------------------------------------------------------------------- */
  /*                               RENDERING                               */
  /* --------------------------------------------------------------------- */
  return (
    <div className="space-y-6">
      {/* Botón para crear nuevo resultado */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => {
            setEditingResult(null);
            setResultForm({
              game_id: '',
              home_team: '',
              away_team: '',
              home_score: 0,
              away_score: 0,
              status: 'final',
            });
            setShowResultForm(true);
          }}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          <i className="ri-add-line mr-2"></i>
          Añadir Resultado
        </button>
      </div>

      {/* Listado de resultados */}
      {results.length === 0 ? (
        <p className="text-gray-400">No hay resultados registrados</p>
      ) : (
        <div className="space-y-4">
          {results.map((result) => {
            const resultInfo = getResultType(result.home_team, result.away_team, result.home_score, result.away_score);
            
            return (
              <div
                key={result.id}
                className="bg-gray-700 border border-gray-600 rounded-lg p-4"
              >
                <div className="flex flex-col space-y-3">
                  {/* Encabezado con jornada, fecha y estado */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {result.jornada && (
                        <span className="bg-orange-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                          Jornada {result.jornada}
                        </span>
                      )}
                      <span className="text-gray-300 text-sm">
                        {new Date(result.date || result.created_at).toLocaleDateString(
                          'es-ES',
                          {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          }
                        )}
                      </span>
                      <span className="bg-green-600 text-white px-2 py-1 rounded-full text-xs">
                        {result.status === 'final' ? 'Final' : result.status}
                      </span>
                    </div>

                    {/* Botones de acción */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => editResult(result)}
                        className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
                        title="Editar resultado"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button
                        onClick={() => deleteResult(result.id)}
                        className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 transition-colors"
                        title="Eliminar resultado"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>

                  {/* Detalle del marcador */}
                  <div className="bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      {/* Equipo local */}
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="flex-1">
                          <p className="text-white font-medium text-sm">{result.home_team}</p>
                          <p className="text-gray-400 text-xs">Local</p>
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-white">{result.home_score}</span>
                        </div>
                      </div>

                      {/* Separador VS */}
                      <div className="px-4">
                        <span className="text-gray-400 font-medium">VS</span>
                      </div>

                      {/* Equipo visitante */}
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="text-left">
                          <span className="text-2xl font-bold text-white">{result.away_score}</span>
                        </div>
                        <div className="flex-1 text-right">
                          <p className="text-white font-medium text-sm">{result.away_team}</p>
                          <p className="text-gray-400 text-xs">Visitante</p>
                        </div>
                      </div>
                    </div>

                    {/* Resultado textual (victoria/empate/derrota) */}
                    <div className="text-center mt-3">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                          resultInfo.type === 'victoria'
                            ? 'bg-green-600 text-white'
                            : resultInfo.type === 'derrota'
                            ? 'bg-red-600 text-white'
                            : resultInfo.type === 'empate'
                            ? 'bg-gray-600 text-white'
                            : 'bg-blue-600 text-white'
                        }`}
                      >
                        {resultInfo.text}
                      </span>
                    </div>
                  </div>

                  {/* Información extra (ubicación) */}
                  {result.location && (
                    <div className="text-center">
                      <span className="text-gray-400 text-sm">
                        <i className="ri-map-pin-line mr-1"></i>
                        {result.location}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal para crear / editar resultados */}
      {showResultForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-4 text-white">
              {editingResult ? 'Editar Resultado' : 'Añadir Resultado'}
            </h3>
            <form onSubmit={handleResultSubmit}>
              {/* Selección de partido */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Seleccionar Partido (Solo partidos ya jugados)
                </label>
                <select
                  value={resultForm.game_id}
                  onChange={(e) => handleGameSelect(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">
                    Seleccionar partido o ingresar manualmente
                  </option>
                  {playedGames.map((game) => (
                    <option key={game.id} value={game.id}>
                      {game.jornada ? `Jornada ${game.jornada} - ` : ''}
                      {game.home_team} vs {game.away_team} -{' '}
                      {new Date(game.date).toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}{' '}
                      {game.time}
                    </option>
                  ))}
                </select>
                {games.length > 0 && playedGames.length === 0 && (
                  <p className="text-yellow-400 text-sm mt-2">
                    <i className="ri-information-line mr-1"></i>
                    No hay partidos jugados disponibles para añadir resultados
                  </p>
                )}
              </div>

              {/* Selección de equipos */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Equipo Local *
                  </label>
                  <select
                    value={resultForm.home_team}
                    onChange={(e) =>
                      setResultForm({ ...resultForm, home_team: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">Seleccionar equipo</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.name}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Equipo Visitante *
                  </label>
                  <select
                    value={resultForm.away_team}
                    onChange={(e) =>
                      setResultForm({ ...resultForm, away_team: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">Seleccionar equipo</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.name}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Puntuaciones */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Puntos Local *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={resultForm.home_score}
                    onChange={(e) =>
                      setResultForm({
                        ...resultForm,
                        home_score: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Puntos Visitante *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={resultForm.away_score}
                    onChange={(e) =>
                      setResultForm({
                        ...resultForm,
                        away_score: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
              </div>

              {/* Estado del juego */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Estado del partido
                </label>
                <select
                  value={resultForm.status}
                  onChange={(e) =>
                    setResultForm({ ...resultForm, status: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="final">Finalizado</option>
                  <option value="postponed">Aplazado</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </div>

              {/* Botones de acción */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowResultForm(false)}
                  className="px-4 py-2 border border-gray-600 text-gray-300 rounded-md hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {editingResult ? 'Actualizar' : 'Crear'} Resultado
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
